import { DirectSubChannel, SensorPortCommunicationMode } from "../../../portConfigs.js";
import { SensorClusterConfig, SensorElementOutput } from "../types.js";

// ─── Verdi F1 series (PWM flowmeters) ────────────────────────────────────────
//
// Same calibration format as Badger Meter: result [L/min] = kFactor_old * (freq + offset)
// where kFactor_old has effective units of (L/min)/Hz.

type VerdiFlowmeterParams = {
    /** Legacy k-factor in (L/min)/Hz — divided by 60 to get L/pulse for the element */
    kFactor_old: number;
    /** Frequency offset (Hz) from the calibration curve */
    frequencyOffset: number;
    minRawValueHz: number;
    modelName: string;
    friendlyName: string;
    friendlyShortName: string;
};

function verdiFlowmeterCluster({
    kFactor_old,
    frequencyOffset,
    minRawValueHz,
    modelName,
    friendlyName,
    friendlyShortName,
}: VerdiFlowmeterParams): SensorClusterConfig<Record<string, never>> {
    const kFactor = kFactor_old / 60;

    const sensorElements: SensorElementOutput<"flowmeter">[] = [
        {
            sensorElementConfigKey: "flowmeter",
            parameters: { kFactor, minimumFrequency: minRawValueHz, frequencyOffset },
            legalConvertedRange: [0, null],
            subChannel: DirectSubChannel.PULSE_FREQUENCY,
        },
    ];

    return {
        communicationMode: SensorPortCommunicationMode.DIRECT,
        requiredSubChannels: [DirectSubChannel.PULSE_FREQUENCY],
        optionalSubChannels: [],
        isPermanentlyAttached: false,
        parameterConfigs: {},
        manufacturerName: "Verdi",
        modelName,
        friendlyName,
        friendlyShortName,
        generateSensorElements: () => sensorElements,
    };
}

export const verdiFlowmeterClusterConfigs: Record<string, SensorClusterConfig<Record<string, never>>> = {
    // Verdi F1 3/4"
    v1flowmeter: {
        ...verdiFlowmeterCluster({
            kFactor_old: 0.128,
            frequencyOffset: 0.509375,
            minRawValueHz: 2,
            modelName: `F1 3/4"`,
            friendlyName: `3/4" Flowmeter`,
            friendlyShortName: `3/4" Flow`,
        }),
        migrationProperties: {
            legacySensorType: "v1flowmeter",
            // Both the legacy sensor and the cluster have no configurable params.
            legacyParametersToClusterParameters: () => ({}),
            clusterParametersToLegacyParameters: () => ({}),
        },
    },

    // Verdi F1 1/4"
    verdiFlowmeterQuarterInch: {
        ...verdiFlowmeterCluster({
            kFactor_old: 0.00726,
            frequencyOffset: 27.2727273,
            minRawValueHz: 20,
            modelName: `F1 1/4"`,
            friendlyName: `1/4" Flowmeter`,
            friendlyShortName: `1/4" Flow`,
        }),
        migrationProperties: {
            legacySensorType: "verdiFlowmeterQuarterInch",
            legacyParametersToClusterParameters: () => ({}),
            clusterParametersToLegacyParameters: () => ({}),
        },
    },
};
